import {useToastContext} from '@github-ui/toast'
import {ActionList, ActionMenu, Box, Button, FormControl, Link, Portal, Radio, RadioGroup, Text} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import type {AbuseReportReason} from '../../mutations/__generated__/submitAbuseReportMutation.graphql'
import {submitAbuseReportMutation} from '../../mutations/submit-abuse-report-mutation'

export type ReportContentDialogProps = {
  owner: string
  ownerUrl: string
  reportUrl?: string
  contentId: string
  onClose: () => void
  contentType?: ContentType
}

type ReportReason = AbuseReportReason | 'Choose a reason'
type ReportType = 'admins' | 'support'
type ContentType = 'issue' | 'comment' | 'pull request' | 'content'

export const ReportContentDialog = ({
  owner,
  ownerUrl,
  reportUrl,
  contentId,
  onClose,
  contentType = 'content',
}: ReportContentDialogProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [reportReason, setReportReason] = useState<ReportReason>('Choose a reason')
  const [isLoading, setIsLoading] = useState(false)
  const [reportType, setReportType] = useState<ReportType>('admins')

  const onSubmitReportToRepositoryAdmins = useCallback(() => {
    setIsLoading(true)
    const reason = getReason(reportReason)

    submitAbuseReportMutation({
      environment,
      input: {
        reportedContentId: contentId,
        reason,
      },
      onError(error) {
        addToast({
          type: 'error',
          message: `Unable to submit report: ${error}`,
        })
        setIsLoading(false)
      },
      onCompleted() {
        addToast({
          type: 'success',
          message: 'Report submitted',
        })
        setIsLoading(false)
        onClose()
      },
    })
  }, [addToast, contentId, environment, onClose, reportReason])

  const handleReportTypeChange = (selectedValue: string | null) => {
    setReportType(selectedValue as ReportType)
  }

  const classForAdmins = reportType !== 'admins' ? {className: 'sr-only'} : {}
  const classForSupport = reportType !== 'support' ? {className: 'sr-only'} : {}

  const reportToMaintainers = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      {...classForAdmins}
    >
      <Box>
        <Text>This {contentType} violates </Text>
        <Link href={ownerUrl}>{`@${owner}'s `}</Link>
        <Text>
          Code of Conduct and should be submitted to the maintainers of this repository for review.
          <br />
          <br />
          This report, as well as your username as the reporter, will be viewable by all admins of this repository.
          <br />
          <br />
          Choose a reason for reporting this {contentType}
        </Text>
      </Box>
      <Box
        sx={{
          marginTop: '8px',
          marginBottom: '16px',
        }}
      >
        <ActionMenu>
          <ActionMenu.Button>{reportReason}</ActionMenu.Button>

          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={() => setReportReason('ABUSE')}>Abuse</ActionList.Item>
              <ActionList.Item onSelect={() => setReportReason('SPAM')}>Spam</ActionList.Item>
              <ActionList.Item onSelect={() => setReportReason('OFF_TOPIC')}>Off Topic</ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </Box>
      <Box
        sx={{
          marginTop: '8px',
        }}
      >
        <Box
          sx={{
            fontSize: '12px',
            color: 'fg.muted',
          }}
        >
          <Text>Learn more about </Text>
          <Link href="https://docs.github.com/en/articles/reporting-abuse-or-spam">
            requesting that maintainers moderate content.
          </Link>
        </Box>
        <Button
          sx={{
            marginTop: '30px',
            width: '100%',
          }}
          variant={'danger'}
          onClick={() => onSubmitReportToRepositoryAdmins()}
          disabled={isLoading}
        >
          Report to repository admins
        </Button>
      </Box>
    </Box>
  )

  const reportToSupport = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      {...classForSupport}
    >
      <Text>Contact GitHub Support about this user&apos;s spammy or abusive behavior.</Text>
      <Box
        sx={{
          marginTop: '12px',
        }}
      >
        <Box
          sx={{
            marginTop: 'auto',
            fontSize: '12px',
            color: 'fg.muted',
          }}
        >
          <Text>Learn more about </Text>
          <Link href="https://docs.github.com/en/articles/reporting-abuse-or-spam">
            reporting abuse to GitHub support
          </Link>
        </Box>
        <Button
          sx={{
            width: '100%',
            marginTop: '30px',
          }}
          variant={'danger'}
          as={'a'}
          href={reportUrl}
          disabled={isLoading}
        >
          Report abuse to GitHub support
        </Button>
      </Box>
    </Box>
  )

  return (
    <Portal>
      <Dialog
        renderHeader={() => (
          <Dialog.Header>
            <Box sx={{display: 'flex'}}>
              <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, px: 2, py: '6px'}}>
                <Dialog.Title>Report {contentType}</Dialog.Title>
              </Box>
              <Dialog.CloseButton onClose={onClose} />
            </Box>
          </Dialog.Header>
        )}
        sx={{
          width: '100%',
          margin: 4,
          maxWidth: '640px',
          maxHeight: 'clamp(315px, 80vh, 800px)',
        }}
        width="xlarge"
        height="auto"
        onClose={onClose}
      >
        <Box sx={{gap: 3, display: 'grid'}}>
          <RadioGroup name="choiceGroup" onChange={handleReportTypeChange}>
            <RadioGroup.Label sx={{fontWeight: 'bold', fontSize: '14px', mb: '4px'}}>
              Where would you like to report this {contentType} to?
            </RadioGroup.Label>
            <FormControl>
              <Radio value="admins" defaultChecked aria-describedby={'Report to repoistory admins'} />
              <FormControl.Label>To repository admins</FormControl.Label>
            </FormControl>
            <FormControl>
              <Radio value="support" aria-describedby={'Report to GitHub support'} />
              <FormControl.Label>To GitHub support</FormControl.Label>
            </FormControl>
          </RadioGroup>
          {reportToMaintainers}
          {reportToSupport}
        </Box>
      </Dialog>
    </Portal>
  )
}

function getReason(reportReason: ReportReason): AbuseReportReason {
  if (reportReason === 'Choose a reason') {
    return 'UNSPECIFIED'
  }
  return reportReason
}

try{ ReportContentDialog.displayName ||= 'ReportContentDialog' } catch {}